import { useState, useRef, useEffect, useMemo } from 'react';
import { 
  type Guide,
  type GuideContentRequest,
  type GuideTranslationRequest,
  type GuideUpsertRequest,
  type GuideStatus,
  type RootVehicleRequest,
  type VehicleFilterRequest,
  type EngineType
} from '@/features/guides/api/generated';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';

export type EditableGuideState = {
  content: GuideContentRequest;
  rootVehicle: Partial<RootVehicleRequest>;
  filters: VehicleFilterRequest[];
  sourceLocale: string;
  status?: GuideStatus;
  translatedToEnglish?: GuideContentRequest;
  translations?: GuideTranslationRequest[] | null;
};

export const useGuideFormState = (data: { guide?: Guide } | undefined) => {
  const [formState, setFormState] = useState<EditableGuideState | null>(null);
  const initialFormStateRef = useRef<EditableGuideState | null>(null);

  useEffect(() => {
    const guide: Guide | undefined = data?.guide;
    if (guide && !formState) {
      const initialState: EditableGuideState = {
        content: {
          problem: guide.content.problem,
          possibleCause: guide.content.possibleCause || undefined,
          solutions: guide.content.solutions || [],
        } as GuideContentRequest,
        rootVehicle: guide.rootVehicle ? {
          engineBaseType: guide.rootVehicle.engineBaseType as EngineType | undefined,
          euroCode: guide.rootVehicle.euroCode ?? undefined,
          manufacturer: guide.rootVehicle.manufacturer ?? undefined,
          modelSeries: guide.rootVehicle.modelSeries ?? undefined,
          modelYear: guide.rootVehicle.modelYear ?? undefined,
        } : {},
        filters: (guide.filters || []).map(f => ({
          engineBaseType: f.engineBaseType as EngineType | undefined,
          euroCode: f.euroCode ?? undefined,
          manufacturer: f.manufacturer ?? undefined,
          modelSeries: f.modelSeries ?? undefined,
          modelYearMax: f.modelYearMax ?? undefined,
          modelYearMin: f.modelYearMin ?? undefined,
        })),
        sourceLocale: guide.sourceLocale,
        status: guide.status,
        translatedToEnglish: guide.translatedToEnglish ? {
          problem: guide.translatedToEnglish.problem,
          possibleCause: guide.translatedToEnglish.possibleCause || undefined,
          solutions: guide.translatedToEnglish.solutions,
        } : undefined,
        translations: (guide.translations || [])?.map(t => ({
          locale: t.locale,
          problem: t.problem,
          possibleCause: t.possibleCause || undefined,
          solutions: t.solutions,
        })),
      };
      setFormState(initialState);
      initialFormStateRef.current = JSON.parse(JSON.stringify(initialState));
    }
  }, [data, formState]);

  const isDirty = useMemo(() => {
    if (!formState || !initialFormStateRef.current) return false;
    return JSON.stringify(formState) !== JSON.stringify(initialFormStateRef.current);
  }, [formState]);

  const updateContentField = (key: keyof GuideContentRequest, value: unknown) => {
    setFormState(prev => prev ? { ...prev, content: { ...prev.content, [key]: value } } : prev);
  };

  const updateRootVehicleWithSelection = (selection: VehicleSelectionValue) => {
    setFormState(prev => prev ? {
      ...prev,
      rootVehicle: {
        ...prev.rootVehicle,
        engineBaseType: selection.engineBaseType as EngineType | undefined,
        euroCode: selection.euroCode ?? undefined,
        manufacturer: selection.manufacturer,
        modelSeries: selection.modelSeries,
        modelYear: selection.modelYear,
      }
    } : prev);
  };

  const resetRootVehicle = (guide: Guide) => {
    setFormState(prev => prev ? {
      ...prev,
      rootVehicle: guide.rootVehicle ? {
        engineBaseType: guide.rootVehicle.engineBaseType as EngineType | undefined,
        euroCode: guide.rootVehicle.euroCode ?? undefined,
        manufacturer: guide.rootVehicle.manufacturer ?? undefined,
        modelSeries: guide.rootVehicle.modelSeries ?? undefined,
        modelYear: guide.rootVehicle.modelYear ?? undefined,
      } : {}
    } : prev);
  };

  const addSolution = () => {
    setFormState(prev => prev ? { ...prev, content: { ...prev.content, solutions: [...(prev.content.solutions || []), ''] } } : prev);
  };

  const updateSolution = (index: number, value: string) => {
    setFormState(prev => {
      if (!prev) return prev;
      const next = [...(prev.content.solutions || [])];
      next[index] = value;
      return { ...prev, content: { ...prev.content, solutions: next } };
    });
  };

  const removeSolution = (index: number) => {
    setFormState(prev => {
      if (!prev) return prev;
      const next = [...(prev.content.solutions || [])];
      next.splice(index, 1);
      return { ...prev, content: { ...prev.content, solutions: next } };
    });
  };

  const reorderSolutions = (fromIndex: number, toIndex: number) => {
    setFormState(prev => {
      if (!prev) return prev;
      const list = [...(prev.content.solutions || [])];
      const [moved] = list.splice(fromIndex, 1);
      const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      list.splice(adjustedIndex, 0, moved);
      return { ...prev, content: { ...prev.content, solutions: list } };
    });
  };

  const addFilter = (newFilter: VehicleFilterRequest) => {
    setFormState(prev => prev ? { ...prev, filters: [...(prev.filters || []), newFilter] } : prev);
  };

  const removeFilterAt = (index: number) => {
    setFormState(prev => {
      if (!prev) return prev;
      const next = [...(prev.filters || [])];
      next.splice(index, 1);
      return { ...prev, filters: next };
    });
  };

  const updateFilterAt = (index: number, updatedFilter: VehicleFilterRequest) => {
    setFormState(prev => {
      if (!prev) return prev;
      const next = [...(prev.filters || [])];
      next[index] = updatedFilter;
      return { ...prev, filters: next };
    });
  };

  const removeNullValues = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
    const result = {} as Partial<T>;
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'string' && value.trim() === '') {
          // Skip empty strings too
          return;
        }
        result[key as keyof T] = value as T[keyof T];
      }
    });
    return result;
  };

  const createSavePayload = (id: string): GuideUpsertRequest | null => {
    if (!formState) return null;
    
    // Clean rootVehicle by removing null/undefined/empty values
    const cleanRootVehicle = removeNullValues(formState.rootVehicle);
    
    // Clean filters by removing null/undefined/empty values from each filter
    const cleanFilters = formState.filters.map(filter => removeNullValues(filter as Record<string, unknown>));
    
    return {
      content: formState.content,
      filters: cleanFilters,
      rootVehicle: cleanRootVehicle,
      sourceLocale: formState.sourceLocale,
      status: formState.status,
      translatedToEnglish: formState.translatedToEnglish,
      translations: formState.translations || undefined,
      id,
    } as GuideUpsertRequest;
  };

  const markAsSaved = () => {
    if (formState) {
      initialFormStateRef.current = JSON.parse(JSON.stringify(formState));
    }
  };

  const updateStatus = (status: GuideStatus) => {
    setFormState(prev => prev ? { ...prev, status } : prev);
  };

  return {
    formState,
    isDirty,
    updateContentField,
    updateRootVehicleWithSelection,
    resetRootVehicle,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
    createSavePayload,
    markAsSaved,
    updateStatus,
  };
};